import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useApi, useAuth, useTranslations } from "../../../../contexts";
import { client } from "../../../../helpers";
import { routerPaths } from "../../../routerPaths";
import { ClientSitePatchBody } from "../SiteEditPage";
import { LoadingErrorState } from "../../../../components";
import { ImageUploadMetadata } from "../../../../components/ImageUpload";
import { SidebarContactForm } from "./SidebarContactForm";
import { SidebarFormPhotos } from "./SidebarFormPhotos";
import { SidebarFormServices } from "./SidebarFormServices";
import { SidebarFormFaq } from "./SidebarFormFaq";
import { SidebarFormText } from "./SidebarFormText";
import { SidebarFormTeams } from "./SidebarFormTeams";
import { SidebarNavbar } from "./SidebarNavbar";
import { SideFormHero } from "./SideFormHero";
import { SidebarColors } from "./SidebarColors";
import { AsyncStatus } from "../../../../helpers/asyncStatus";
import { prepareSiteFormPayload } from "./siteFormPayload";

export interface SidebarFormProps {
  form: ClientSitePatchBody & { id?: string };
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  setForm: Dispatch<SetStateAction<ClientSitePatchBody | undefined>>;
  siteId: string;
  imageMetadata: Record<string, ImageUploadMetadata>;
  setImageMetadata: Dispatch<SetStateAction<Record<string, ImageUploadMetadata>>>;
}

export const SidebarForm: React.FC<SidebarFormProps> = (props) => {
  const i18n = useTranslations();
  const auth = useAuth();
  const api = useApi();
  const [submitStatus, setSubmitStatus] = useState<AsyncStatus>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isLoading = submitStatus === "loading";
  const { siteId } = useParams<Parameters<typeof routerPaths.siteEdit>["0"]>();
  if (!siteId) throw new Error(`site id missing`);

  const saveForm = async () => {
    if (!props.form || isLoading) return;
    setSubmitStatus("loading");
    setSubmitError(null);
    try {
      const formPayload = prepareSiteFormPayload(props.form);
      const results = await (
        await client.api.sites[":siteId"].$patch({
          param: { siteId },
          json: { ...formPayload, image_metadata: props.imageMetadata },
          header: auth.header,
        })
      ).json();
      if (results.success) {
        setSubmitStatus("success");
        props.setIsDirty(false);
        toast.success(i18n["Site updated"]);
        api.queryClient.invalidateQueries({
          queryKey: ["site", siteId],
        });
      } else {
        setSubmitStatus("error");
        setSubmitError(i18n["Something went wrong"]);
        toast.error(i18n["Something went wrong"]);
      }
    } catch (error) {
      console.error(error);
      setSubmitStatus("error");
      setSubmitError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    await saveForm();
  };

  return (
    <form className="h-full border-e" onSubmit={handleSubmit} aria-busy={isLoading}>
      <SidebarNavbar {...props} loading={isLoading} />
      {submitError && (
        <LoadingErrorState
          className="m-4"
          message={submitError}
          retryLabel={i18n["Retry"]}
          onRetry={() => saveForm()}
        />
      )}
      <SidebarColors {...props} />
      <SideFormHero {...props} siteId={siteId} />
      <SidebarFormServices {...props} />
      <SidebarFormPhotos {...props} siteId={siteId} />
      <SidebarFormTeams {...props} siteId={siteId} />
      <SidebarFormText {...props} />
      <SidebarFormFaq {...props} />
      <SidebarContactForm {...props} />
    </form>
  );
};
